import { useState } from 'react'
import { Link, useLocation } from 'react-router'
import { Radio, Database, Globe, FileUp, Pencil, Plus, RefreshCw, Users, Info, CalendarClock, Settings2 } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import {
  ConfirmDialog, DateValue, EmptyState, ErrorState, FormDialog, PageHeader, PermissionGate,
  StatusBadge,
} from '@/components/common'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select } from '@/components/ui/select'
import { useLocations } from '@/features/org'
import { toApiProblem } from '@/lib/api/client'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { useDevices, useDeviceMutations, useSources, useSourceMutations, useRecentPunches } from '../api'
import { SourceForm, type SourceFormValues } from '../components/SourceForm'
import { SourceActions } from '../components/SourceActions'
import { SourceRunsTable } from '../components/SourceRunsTable'
import { PunchFeed } from '../components/PunchFeed'
import type { SourceType } from '../types'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { cn } from '@/lib/utils'

const SOURCE_ICONS: Record<SourceType, LucideIcon> = {
  ADMS_PUSH: Radio,
  SQL_DATABASE: Database,
  BIOTIME_REST: Globe,
  CSV_IMPORT: FileUp,
}

const SOURCE_LABELS: Record<SourceType, string> = {
  ADMS_PUSH: 'Device push',
  SQL_DATABASE: 'Vendor database',
  BIOTIME_REST: 'Vendor API',
  CSV_IMPORT: 'File upload',
}

type Tab = 'devices' | 'sources' | 'feed'

/**
 * Attendance device administration.
 *
 * <p>Three tabs because commissioning a terminal is three distinct jobs: decide where
 * punches come from, approve the hardware, then watch punches land to confirm it works.
 */
export function AttendanceDevicesPage() {
  // The sidebar has three links here and they all resolve to this page, so the tab has to
  // come from the URL. Defaulting to Devices meant "Punch sources" took you to Devices —
  // the link worked and went somewhere else.
  const { pathname } = useLocation()
  const [tab, setTab] = useState<Tab>(() =>
    pathname.endsWith('/sources') ? 'sources' : pathname.endsWith('/feed') ? 'feed' : 'devices',
  )
  const breadcrumb = useBreadcrumb()

  const devices = useDevices()
  const sources = useSources()
  const punches = useRecentPunches(undefined, 50)
  const deviceActions = useDeviceMutations()
  const sourceActions = useSourceMutations()

  const [showSourceForm, setShowSourceForm] = useState(false)
  const [editingDevice, setEditingDevice] = useState<{
    id: string; name: string; model: string; ipAddress: string; locationId: string;
    timezone: string
  } | null>(null)
  const [disabling, setDisabling] = useState<{ id: string; name: string } | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const locations = useLocations()
  const [expandedSource, setExpandedSource] = useState<string | null>(null)

  function addSource(values: SourceFormValues) {
    sourceActions.create.mutate(values as unknown as Record<string, unknown>, {
      onSuccess: () => setShowSourceForm(false),
    })
  }

  const onlineCount = devices.data?.filter((device) => device.online).length ?? 0
  const pendingCount = devices.data?.filter((device) => device.status === 'PENDING').length ?? 0

  return (
    <div className="space-y-4">
      <PageHeader
        title="Attendance devices"
        subtitle="Biometric terminals and where punch data comes from"
        breadcrumb={breadcrumb}
        actions={
          <div className="flex gap-2">
            <Button variant="outline" asChild>
              <Link to="/attendance/roster">
                <CalendarClock aria-hidden="true" />
                Roster
              </Link>
            </Button>
            <Button variant="outline" onClick={() => void devices.refetch()}>
              <RefreshCw aria-hidden="true" />
              Refresh
            </Button>
          </div>
        }
      />

      {pendingCount > 0 && (
        <div
          role="status"
          className="flex items-start gap-2 rounded-[var(--radius-card)] bg-[var(--color-tint-orange)] px-4 py-3 text-sm"
        >
          <Info className="mt-0.5 size-4 shrink-0 text-[var(--color-warning)]" aria-hidden="true" />
          <span className="text-[var(--color-body)]">
            <strong className="text-[var(--color-heading)]">
              {pendingCount} device{pendingCount === 1 ? '' : 's'} awaiting approval.
            </strong>{' '}
            A terminal's punches are ignored until it is approved, so plugging one in never
            silently starts feeding payroll.
          </span>
        </div>
      )}

      <nav className="flex gap-1 border-b border-[var(--color-border)]" aria-label="Device sections">
        {(
          [
            ['devices', `Devices${devices.data ? ` (${onlineCount}/${devices.data.length} online)` : ''}`],
            ['sources', 'Punch sources'],
            ['feed', 'Live feed'],
          ] as [Tab, string][]
        ).map(([value, label]) => (
          <button
            key={value}
            type="button"
            onClick={() => setTab(value)}
            aria-current={tab === value ? 'page' : undefined}
            className={cn(
              '-mb-px border-b-2 px-4 py-2.5 text-sm font-medium transition-colors',
              tab === value
                ? 'border-[var(--color-primary)] text-[var(--color-primary)]'
                : 'border-transparent text-[var(--color-body)] hover:text-[var(--color-heading)]',
            )}
          >
            {label}
          </button>
        ))}
      </nav>

      {/* ---------------- Devices ---------------- */}
      {tab === 'devices' && (
        <>
          {devices.isLoading && <Skeleton className="h-48 w-full rounded-[var(--radius-card)]" />}
          {devices.error != null && <ErrorState error={devices.error} onRetry={() => void devices.refetch()} />}
          {devices.data?.length === 0 && (
            <Card>
              <EmptyState
                icon={Radio}
                title="No devices yet"
                description="Point a terminal's ADMS setting at this server, or add a vendor database or API source."
                action={<Button onClick={() => setTab('sources')}>Configure a source</Button>}
              />
            </Card>
          )}

          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {devices.data?.map((device) => (
              <Card key={device.id} className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate font-medium text-[var(--color-heading)]">{device.name}</p>
                    <p className="truncate font-mono text-xs text-[var(--color-muted)]">{device.serialNumber}</p>
                  </div>
                  <span className="flex shrink-0 items-center gap-1.5">
                    <span
                      className={cn(
                        'size-2 rounded-full',
                        device.online ? 'bg-[var(--color-success)]' : 'bg-[var(--color-muted)]',
                      )}
                      aria-hidden="true"
                    />
                    <span className="text-xs text-[var(--color-muted)]">
                      {device.online ? 'Online' : 'Offline'}
                    </span>
                  </span>
                </div>

                <dl className="mt-3 space-y-1 text-xs">
                  <div className="flex justify-between gap-2">
                    <dt className="text-[var(--color-muted)]">Status</dt>
                    <dd><StatusBadge status={device.status} /></dd>
                  </div>
                  {device.model && (
                    <div className="flex justify-between gap-2">
                      <dt className="text-[var(--color-muted)]">Model</dt>
                      <dd className="truncate text-[var(--color-heading)]">{device.model}</dd>
                    </div>
                  )}
                  <div className="flex justify-between gap-2">
                    <dt className="text-[var(--color-muted)]">Last seen</dt>
                    <dd className="text-[var(--color-heading)]">
                      {device.lastSeenAt ? <DateValue value={device.lastSeenAt} relative /> : 'Never'}
                    </dd>
                  </div>
                  <div className="flex justify-between gap-2">
                    <dt className="text-[var(--color-muted)]">Users on device</dt>
                    <dd className="tabular-nums text-[var(--color-heading)]">{device.userCount ?? '—'}</dd>
                  </div>
                </dl>

                <PermissionGate permission="device:manage">
                  <div className="mt-3 flex flex-wrap gap-2">
                    {device.status === 'PENDING' && (
                      <Button size="sm" onClick={() => deviceActions.approve.mutate(device.id)}>
                        Approve
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => deviceActions.queueCommand.mutate({ id: device.id, type: 'INFO' })}
                    >
                      Query info
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => deviceActions.pushUsers.mutate(device.id)}
                    >
                      <Users aria-hidden="true" />
                      Push users
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      aria-label={`Edit ${device.name}`}
                      onClick={() => {
                        setActionError(null)
                        setEditingDevice({
                          id: device.id,
                          name: device.name,
                          model: device.model ?? '',
                          ipAddress: device.ipAddress ?? '',
                          locationId: device.locationId ?? '',
                          timezone: '',
                        })
                      }}
                    >
                      <Pencil aria-hidden="true" />
                      Edit
                    </Button>
                    {device.status !== 'DISABLED' && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => { setActionError(null); setDisabling(device) }}
                      >
                        Disable
                      </Button>
                    )}
                  </div>
                </PermissionGate>
              </Card>
            ))}
          </div>
        </>
      )}

      {/* ---------------- Sources ---------------- */}
      {tab === 'sources' && (
        <div className="space-y-4">
          <PermissionGate permission="device:manage">
            <div className="flex justify-end">
              <Button onClick={() => setShowSourceForm((open) => !open)}>
                <Plus aria-hidden="true" />
                {showSourceForm ? 'Cancel' : 'Add source'}
              </Button>
            </div>
          </PermissionGate>

          {showSourceForm && (
            <Card className="p-5">
              <h3 className="mb-4 text-base font-semibold text-[var(--color-heading)]">New punch source</h3>
              <SourceForm onSubmit={addSource} submitting={sourceActions.create.isPending} />
            </Card>
          )}

          {sources.data?.length === 0 && !showSourceForm && (
            <Card>
              <EmptyState
                icon={Database}
                title="No sources configured"
                description="Choose how punches reach the system: terminals pushing here, or reading the vendor's database or API."
              />
            </Card>
          )}

          <div className="space-y-3">
            {sources.data?.map((source) => {
              const Icon = SOURCE_ICONS[source.type]
              return (
                <Card key={source.id} className="p-4">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="flex min-w-0 gap-3">
                      <span className="grid size-10 shrink-0 place-items-center rounded-[var(--radius-control)] bg-[var(--color-primary-soft)]">
                        <Icon className="size-5 text-[var(--color-primary)]" aria-hidden="true" />
                      </span>
                      <div className="min-w-0">
                        <p className="font-medium text-[var(--color-heading)]">{source.name}</p>
                        <p className="text-xs text-[var(--color-muted)]">
                          {SOURCE_LABELS[source.type]}
                          {source.sqlDialect && ` · ${source.sqlDialect.replace(/_/g, ' ')}`}
                          {source.jdbcUrl && ` · ${source.jdbcUrl}`}
                          {source.apiBaseUrl && ` · ${source.apiBaseUrl}`}
                        </p>
                        {source.lastRunAt && (
                          <p className="mt-1 text-xs text-[var(--color-muted)]">
                            Last run <DateValue value={source.lastRunAt} relative /> ·{' '}
                            <span
                              className={
                                source.lastRunStatus === 'OK'
                                  ? 'text-[var(--color-success)]'
                                  : 'text-[var(--color-danger)]'
                              }
                            >
                              {source.lastRunMessage ?? source.lastRunStatus}
                            </span>
                          </p>
                        )}
                      </div>
                    </div>

                    <PermissionGate permission="device:manage">
                      <div className="shrink-0">
                        <Button
                          size="sm"
                          variant="ghost"
                          aria-expanded={expandedSource === source.id}
                          onClick={() =>
                            setExpandedSource(expandedSource === source.id ? null : source.id)
                          }
                        >
                          <Settings2 aria-hidden="true" />
                          {expandedSource === source.id ? 'Hide' : 'Manage'}
                        </Button>
                      </div>
                    </PermissionGate>
                  </div>

                  {expandedSource === source.id && (
                    <PermissionGate permission="device:manage">
                      <div className="mt-4 space-y-4 border-t border-[var(--color-border)] pt-4">
                        <SourceActions source={source} />
                        <div>
                          <h4 className="mb-1 text-sm font-medium text-[var(--color-heading)]">
                            Sync history
                          </h4>
                          <SourceRunsTable sourceId={source.id} />
                        </div>
                      </div>
                    </PermissionGate>
                  )}
                </Card>
              )
            })}
          </div>
        </div>
      )}

      {/* ---------------- Live feed ---------------- */}
      {tab === 'feed' && (
        <PunchFeed
          punches={punches.data}
          isLoading={punches.isLoading}
          error={punches.error}
          onRetry={() => void punches.refetch()}
        />
      )}

      <FormDialog
        open={editingDevice !== null}
        onOpenChange={(open) => { if (!open) setEditingDevice(null) }}
        title={`Edit ${editingDevice?.name ?? 'device'}`}
        description="What a person knows about the machine. Not its serial number, protocol or comm key — those are how the device identifies itself, and editing them would attribute its punches to the wrong machine."
        submitLabel="Save device"
        loading={deviceActions.update.isPending}
        error={actionError}
        onSubmit={() => {
          if (!editingDevice) return
          setActionError(null)
          deviceActions.update.mutate({
            id: editingDevice.id,
            name: editingDevice.name,
            model: editingDevice.model || null,
            ipAddress: editingDevice.ipAddress || null,
            locationId: editingDevice.locationId || null,
            timezone: editingDevice.timezone || null,
          }, {
            onSuccess: () => setEditingDevice(null),
            onError: (caught) => setActionError(
              toApiProblem(caught)?.detail ?? 'That device could not be saved.'),
          })
        }}
      >
        {editingDevice && (
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="device-name">Name</Label>
              <Input
                id="device-name" value={editingDevice.name}
                onChange={(event) =>
                  setEditingDevice({ ...editingDevice, name: event.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="device-model">Model</Label>
              <Input
                id="device-model" value={editingDevice.model}
                onChange={(event) =>
                  setEditingDevice({ ...editingDevice, model: event.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="device-ip">IP address</Label>
              <Input
                id="device-ip" value={editingDevice.ipAddress}
                onChange={(event) =>
                  setEditingDevice({ ...editingDevice, ipAddress: event.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="device-location">Site</Label>
              <Select
                id="device-location" className="w-full" value={editingDevice.locationId}
                onChange={(event) =>
                  setEditingDevice({ ...editingDevice, locationId: event.target.value })}
              >
                <option value="">Not recorded</option>
                {locations.data?.map((location) => (
                  <option key={location.id} value={location.id}>{location.name}</option>
                ))}
              </Select>
              <p className="text-xs text-[var(--color-muted)]">
                Punches are read in this site's timezone.
              </p>
            </div>
          </div>
        )}
      </FormDialog>

      <ConfirmDialog
        open={disabling !== null}
        onOpenChange={(open) => { if (!open) setDisabling(null) }}
        title={`Disable ${disabling?.name ?? 'this device'}?`}
        description="Its punches stop being accepted. Everything it has already sent stays, because every punch on file names the device it came from — and a month of attendance with no provenance is the first thing anybody asks about when a figure is disputed."
        confirmLabel="Disable it"
        loading={deviceActions.deactivate.isPending}
        error={actionError}
        onConfirm={() => {
          if (!disabling) return
          setActionError(null)
          deviceActions.deactivate.mutate(disabling.id, {
            onSuccess: () => setDisabling(null),
            onError: (caught) => setActionError(
              toApiProblem(caught)?.detail ?? 'That device could not be disabled.'),
          })
        }}
      />
    </div>
  )
}
